import React from 'react';
import { Drawer, Box, Typography, Divider, Button, Alert, TextField, Chip, Autocomplete, ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox } from '@mui/material';
import { bulkEditDryRun, bulkEditExecute, BulkEditRequestBody } from '../../services/bulkEditApi';

interface BulkEditDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedAssetIds: string[];
  categories?: Array<{ _id: string; name: string; parentCategory?: string | { _id: string } | null }>;
  onComplete?: () => void;
}

const BulkEditDrawer: React.FC<BulkEditDrawerProps> = ({ open, onClose, selectedAssetIds, categories = [], onComplete }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<any>(null);

  // Step 1 fields state
  const [titleMode, setTitleMode] = React.useState<'set' | 'clear'>('set');
  const [titleValue, setTitleValue] = React.useState('');
  const [descMode, setDescMode] = React.useState<'set' | 'clear' | 'append'>('set');
  const [descValue, setDescValue] = React.useState('');
  const [tagsMode, setTagsMode] = React.useState<'append' | 'replace' | 'remove' | 'clear'>('append');
  const [tagsValues, setTagsValues] = React.useState<string[]>([]);
  const [mainCategoryId, setMainCategoryId] = React.useState<string>('');
  const [subCategoryId, setSubCategoryId] = React.useState<string>('');
  const [applyDefaults, setApplyDefaults] = React.useState<boolean>(true);

  // TODO: load categories with a lightweight endpoint if needed. For now let user paste an ID.

  const buildBody = React.useCallback((isDryRun: boolean): BulkEditRequestBody => {
    const ops: any[] = [];
    if (titleMode === 'set' && titleValue.trim()) ops.push({ field: 'title', op: 'set', value: titleValue.trim() });
    if (titleMode === 'clear') ops.push({ field: 'title', op: 'clear' });

    if (descMode === 'set' && descValue.trim()) ops.push({ field: 'description', op: 'set', value: descValue });
    if (descMode === 'append' && descValue.trim()) ops.push({ field: 'description', op: 'append', value: descValue });
    if (descMode === 'clear') ops.push({ field: 'description', op: 'clear' });

    if (tagsMode === 'append' && tagsValues.length) ops.push({ field: 'tags', op: 'append', values: tagsValues });
    if (tagsMode === 'replace' && tagsValues.length) ops.push({ field: 'tags', op: 'replace', values: tagsValues });
    if (tagsMode === 'remove' && tagsValues.length) ops.push({ field: 'tags', op: 'remove', values: tagsValues });
    if (tagsMode === 'clear') ops.push({ field: 'tags', op: 'clear' });

    // Category: include main + optional subCategoryId for backend to set both
    if (mainCategoryId) {
      ops.push({ field: 'category', op: 'set', value: mainCategoryId, subCategoryId, applyCategoryTemplateDefaults: applyDefaults });
    } else if (subCategoryId) {
      // Allow subcategory-only update; backend will keep (or infer) main category and replace sub
      ops.push({ field: 'category', op: 'set', subCategoryId });
    }

    return {
      assetIds: selectedAssetIds,
      dryRun: isDryRun,
      operations: ops,
    };
  }, [selectedAssetIds, titleMode, titleValue, descMode, descValue, tagsMode, tagsValues, mainCategoryId, subCategoryId, applyDefaults]);

  // Reset form state when drawer opens to avoid stale values between runs
  React.useEffect(() => {
    if (open) {
      setTitleMode('set');
      setTitleValue('');
      setDescMode('set');
      setDescValue('');
      setTagsMode('append');
      setTagsValues([]);
      setMainCategoryId('');
      setSubCategoryId('');
      setApplyDefaults(true);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const onDryRun = async () => {
    try {
      setLoading(true); setError(null);
      const data = await bulkEditDryRun(buildBody(true));
      setResult(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Dry-run failed');
    } finally { setLoading(false); }
  };

  const onExecute = async () => {
    try {
      setLoading(true); setError(null);
      const data = await bulkEditExecute(buildBody(false));
      setResult(data);
      try {
        if (data && data.summary && data.summary.updated > 0) {
          onComplete && onComplete();
          // Fire a global event as well for any listeners
          window.dispatchEvent(new CustomEvent('bulk-edit-completed'));
        }
      } catch {}
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Execute failed');
    } finally { setLoading(false); }
  };

  const hasOps = Boolean(
    (titleMode === 'clear') || (titleMode === 'set' && titleValue.trim()) ||
    (descMode !== 'set') || (descMode === 'set' && descValue.trim()) ||
    (tagsMode !== 'append') || (tagsValues.length > 0) ||
    mainCategoryId || subCategoryId
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 520 } }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="h6">Bulk‑edit (Skeleton)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Steg 1: Titel, Beskrivning, Taggar, Kategori. Använd Dry‑run för att förhandsgranska vilka fält som skulle uppdateras.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" sx={{ mb: 1 }}>Valda objekt: {selectedAssetIds.length}</Typography>

        {/* Title */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Titel</Typography>
          <ToggleButtonGroup size="small" value={titleMode} exclusive onChange={(_, v) => v && setTitleMode(v)} sx={{ mb: 1 }}>
            <ToggleButton value="set">Set</ToggleButton>
            <ToggleButton value="clear">Clear</ToggleButton>
          </ToggleButtonGroup>
          {titleMode === 'set' && (
            <TextField size="small" fullWidth placeholder="Ny titel" value={titleValue} onChange={e => setTitleValue(e.target.value)} />
          )}
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Beskrivning</Typography>
          <ToggleButtonGroup size="small" value={descMode} exclusive onChange={(_, v) => v && setDescMode(v)} sx={{ mb: 1 }}>
            <ToggleButton value="set">Set</ToggleButton>
            <ToggleButton value="append">Append</ToggleButton>
            <ToggleButton value="clear">Clear</ToggleButton>
          </ToggleButtonGroup>
          {(descMode === 'set' || descMode === 'append') && (
            <TextField size="small" fullWidth multiline minRows={2} placeholder={descMode === 'append' ? 'Text som läggs TILL i slutet' : 'Ny beskrivning'} value={descValue} onChange={e => setDescValue(e.target.value)} />
          )}
        </Box>

        {/* Tags */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Taggar</Typography>
          <ToggleButtonGroup size="small" value={tagsMode} exclusive onChange={(_, v) => v && setTagsMode(v)} sx={{ mb: 1 }}>
            <ToggleButton value="append">Append</ToggleButton>
            <ToggleButton value="replace">Replace</ToggleButton>
            <ToggleButton value="remove">Remove</ToggleButton>
            <ToggleButton value="clear">Clear</ToggleButton>
          </ToggleButtonGroup>
          {tagsMode !== 'clear' && (
            <Autocomplete
              multiple freeSolo options={[]}
              value={tagsValues}
              onChange={(_, v) => setTagsValues(v)}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Skriv taggar och tryck Enter" />}
              renderTags={(value, getTagProps) => value.map((tag, idx) => (
                <Chip {...getTagProps({ index: idx })} key={idx} label={tag} />
              ))}
            />
          )}
        </Box>

        {/* Category */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Kategori</Typography>
          <Autocomplete
            options={(categories || []).filter(c => !c.parentCategory)}
            getOptionLabel={(o) => o?.name || ''}
            value={(categories || []).filter(c => !c.parentCategory).find(c => c._id === mainCategoryId) || null}
            onChange={(_, v) => { setMainCategoryId(v?._id || ''); setSubCategoryId(''); }}
            renderInput={(params) => <TextField {...params} size="small" placeholder="Välj huvudkategori" />}
          />
          {/* Subcategory appears when main is chosen */}
          {mainCategoryId && (
            <Autocomplete
              sx={{ mt: 1 }}
              options={(categories || []).filter(c => {
                const p = (c as any).parentCategory;
                if (!p) return false;
                return (typeof p === 'string' ? p : (p as any)._id) === mainCategoryId;
              })}
              getOptionLabel={(o) => o?.name || ''}
              value={(categories || []).find(c => c._id === subCategoryId) || null}
              onChange={(_, v) => setSubCategoryId(v?._id || '')}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Välj underkategori (valfritt)" />}
            />
          )}
          <FormControlLabel
            control={<Checkbox checked={applyDefaults} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApplyDefaults(e.target.checked)} />}
            label="Apply default template values from category"
            sx={{ mt: 1 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button variant="outlined" onClick={onDryRun} disabled={loading || selectedAssetIds.length === 0 || !hasOps}>Dry‑run</Button>
          <Button variant="contained" onClick={onExecute} disabled={loading || selectedAssetIds.length === 0 || !hasOps}>Kör</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Stäng</Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && (
          <Box sx={{ p: 1, bgcolor: '#f7f7f9', border: '1px solid #eee', borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {/* Compact summary */}
            {result?.summary && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption">
                  Totalt: {result.summary.total} • Uppdateras: {result.summary.updated} • Fel: {result.summary.failed}
                </Typography>
                {result.summary.fields && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    Fält: {Object.entries(result.summary.fields).map(([k,v]) => `${k}:${v}`).join('  ')}
                  </Typography>
                )}
              </Box>
            )}
            {/* First 5 diffs */}
            {Array.isArray(result?.results) && result.results.slice(0, 5).map((r: any) => (
              <Box key={r.assetId} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Asset {r.assetId}</Typography>
                {r.diff ? (
                  <>
                    {r.diff.title && (<Typography variant="caption" sx={{ display:'block' }}>title: "{r.diff.title.before}" → "{r.diff.title.after}"</Typography>)}
                    {r.diff.description && (<Typography variant="caption" sx={{ display:'block' }}>description: "{r.diff.description.before}" → "{r.diff.description.after}"</Typography>)}
                    {r.diff.tags && (
                      <Typography variant="caption" sx={{ display:'block' }}>
                        tags: +[{(r.diff.tags.added||[]).join(', ')}] −[{(r.diff.tags.removed||[]).join(', ')}]
                      </Typography>
                    )}
                    {r.diff.category && (
                      <Typography variant="caption" sx={{ display:'block' }}>category: [{(r.diff.category.before||[]).join(', ')}] → [{(r.diff.category.after||[]).join(', ')}]</Typography>
                    )}
                    {!r.diff.title && !r.diff.description && !r.diff.tags && !r.diff.category && (
                      <Typography variant="caption" sx={{ display:'block', color:'text.secondary' }}>Ingen ändring</Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="caption" sx={{ display:'block', color:'text.secondary' }}>{r.message || 'Ingen ändring'}</Typography>
                )}
              </Box>
            ))}
            {/* Raw JSON for felsökning */}
            <Box sx={{ mt: 1 }}>
              {JSON.stringify(result, null, 2)}
            </Box>
          </Box>
        )}
        <Box sx={{ mt: 'auto' }} />
      </Box>
    </Drawer>
  );
};

export default BulkEditDrawer;


